import { FccPassengerCheckInStatus } from '@backoffice-frontend/graphql/subscription'
import { Divider } from '@backoffice-frontend/patterns'
import { Heading } from '@moia-dev/pace-web'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { AdPassengerRow } from '../components/AdPassengerRow'
import { usePassengerAuthentications } from '../hooks/PassengerAuthentications'
import {
  getBoardingPassengers,
  getDeboardingPassengers,
  getPassengerCount,
  getStayingPassengers,
} from '../utils/passenger-filters'
import type { AdWaypointPassengersWaypointFragment } from './AdWaypointPassengers.hook'

export const AdWaypointPassengers = ({
  waypoint,
  futureWaypoints,
}: {
  className?: string
  waypoint: AdWaypointPassengersWaypointFragment
  futureWaypoints: AdWaypointPassengersWaypointFragment[]
}) => {
  const { t } = useTranslation()

  const boardingPassengers = getBoardingPassengers(waypoint)
  const deboardingPassengers = getDeboardingPassengers(waypoint)
  const stayingPassengers = getStayingPassengers(waypoint, futureWaypoints)

  const boardingPassengerCount = getPassengerCount(boardingPassengers)
  const deboardingPassengerCount = getPassengerCount(deboardingPassengers)
  const stayingPassengerCount = getPassengerCount(stayingPassengers)

  const { getPassengerAuthentication, subscribeToPassengerAuthentications } =
    usePassengerAuthentications()

  subscribeToPassengerAuthentications(waypoint)

  const authStates = boardingPassengers.reduce<
    Record<string, FccPassengerCheckInStatus | undefined>
  >((acc, passenger) => {
    if (passenger.tripIsCancelled) {
      acc[passenger.trip.id] = FccPassengerCheckInStatus.Cancelled
      return acc
    }
    acc[passenger.trip.id] = getPassengerAuthentication(passenger.trip.id)
    return acc
  }, {})

  return (
    <>
      <div
        css={theme => ({
          padding: `${theme.space.Space3} ${theme.space.Space4}`,
        })}
      >
        <Heading
          size="sm"
          css={theme => ({
            marginBottom:
              boardingPassengerCount > 0 ? theme.space.Space3 : undefined,
          })}
        >
          {t('adFleetMap_passengerListBoardingPassengers_caption', {
            count: boardingPassengerCount,
          })}
        </Heading>
        {boardingPassengers.map(
          ({ customer, seats, trip, eta, notShownUpAt }, index) => (
            <Fragment key={trip.id}>
              <AdPassengerRow
                eta={eta}
                customer={customer}
                seats={seats}
                authenticationState={authStates[trip.id]}
                isNoShow={!!notShownUpAt}
              />
              {index !== boardingPassengers.length - 1 && (
                <Divider borderStyle="dashed" spacing={2} />
              )}
            </Fragment>
          ),
        )}
      </div>

      <Divider />

      <div
        css={theme => ({
          padding: `${theme.space.Space3} ${theme.space.Space4}`,
        })}
      >
        <Heading
          size="sm"
          css={theme => ({
            marginBottom:
              deboardingPassengerCount > 0 ? theme.space.Space3 : undefined,
          })}
        >
          {t('adFleetMap_passengerListDeboardingPassengers_caption', {
            count: deboardingPassengerCount,
          })}
        </Heading>
        {deboardingPassengers.map(
          ({ customer, seats, trip, notShownUpAt }, index) => (
            <Fragment key={trip.id}>
              <AdPassengerRow
                customer={customer}
                seats={seats}
                isNoShow={!!notShownUpAt}
              />
              {index !== deboardingPassengers.length - 1 && (
                <Divider borderStyle="dashed" spacing={2} />
              )}
            </Fragment>
          ),
        )}
      </div>

      <Divider />

      <div
        css={theme => ({
          padding: `${theme.space.Space3} ${theme.space.Space4}`,
        })}
      >
        <Heading
          size="sm"
          css={theme => ({
            marginBottom:
              stayingPassengerCount > 0 ? theme.space.Space3 : undefined,
          })}
        >
          {t('adFleetMap_badge_passengerListStayingPassengers_caption', {
            count: stayingPassengerCount,
          })}
        </Heading>
        {stayingPassengers.map(
          ({ customer, seats, trip, notShownUpAt }, index) => (
            <Fragment key={trip.id}>
              <AdPassengerRow
                customer={customer}
                seats={seats}
                isNoShow={!!notShownUpAt}
              />
              {index !== stayingPassengers.length - 1 && (
                <Divider borderStyle="dashed" spacing={2} />
              )}
            </Fragment>
          ),
        )}
      </div>
    </>
  )
}
